import { DictionaryLanguage, DictionaryLanguageTranslation } from 'constants/dictionary';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CandidateStatusGroupEnum, LifecycleStatuses, RolesValues, SortingValue } from '@constants';
import {
  AdministrationRequestApproveState,
  AdministrationSourcingState,
  AdministrationStatusItem,
  AdministrationUnitsState,
  DictionaryService,
  LocalizedShortEmployeeItemResponse,
  MergeLocationsItem,
  newEmployeeMapper,
  ReferralState,
  SelectOption,
  SourcingItem,
  StatusesService,
  ThunkEffects,
  UnitItem,
  useAppDispatch,
  useAppSelector,
  useInfiniteScroll,
  useProfileAPI,
} from '@innowise-group/core';

import {
  clearCurrentStatusAction,
  clearReferralStateAction,
  clearSourcingStateAction,
  clearUnitsStateAction,
  resetEmployeeFromHrmByEmailAction,
  resetEmployeesAdministrationsStateAction,
  selectAllUserAuthDataPermissionsAction,
  selectDictionaryCandidateSourceAction,
  selectDomainTypeAction,
  setDictionaryPagableAction,
  setDictionarySearchValueAction,
  setDictionarySortParamsAction,
  setEmployeeFromHrmToStoreAction,
  setNewEmployeeRoleFromHrmAction,
  setPagableAction,
  unSelectAllUserAuthDataPermissionsAction,
  updateExistingUserRoleAction,
  updateUserSelectedAuthDataPermissionsAction,
} from './administration.actions';
import {
  getAdministrationStateSelector,
  getCitiesByLangAdministrationState,
  getDictionariesAdministrationStateSelector,
  getEmployeesAdministrationStateSelector,
  getEmployeesEmailsOptions,
  getLocationsAdministrationState,
  getReferralsAdministrationManagerState,
  getReferralsAdministrationState,
  getSourcingAdministrationState,
  getSourcingDefaultFormItemById,
  getStatusesAdministrationState,
  getUnitsAdministrationOptions,
  getUnitsAdministrationState,
} from './administration.selectors';
import {
  addEmployeeThunk,
  closeReferralPaymentPeriodThunk,
  closeReferralPaymentThunk,
  createSourcingEmployeeThunk,
  createStatusThunk,
  createUnitThunk,
  deleteEmployeeThunk,
  deleteStatusItemByIdThunk,
  exportReferralDataThunk,
  fetchCitiesByLanguagesThunk,
  getActualUnitsThunk,
  getAdministrationRequestApproveThunk,
  getAllSourcingDataThunk,
  getAllUnitsThunk,
  getReferralDataThunk,
  getReferralManagerOptionsThunk,
  getReferralsManagerThunk,
  getReferralsTotalDataThunk,
  getStatusItemByIdThunk,
  mergeLocationsThunk,
  resolveReferralStatusThunk,
  restoreStatusItemByIdThunk,
  rolePermissionAssignThunk,
  searchDictionaryValuesThunk,
  searchEmailByEmailPartThunk,
  searchEmployeesThunk,
  searchPagableVacanciesOptionsThunk,
  searchRequestOptionsThunk,
  searchVacancyOptionsThunk,
  syncEventFromHrmThunk,
  transferReferralPaymentThunk,
  updateEmployeeAuthDataThunk,
  updateReferralsManagerThunk,
  updateSourcingEmployeeThunk,
  updateStatusThunk,
  updateUnitThunk,
} from './administration.thunk';

export const useAdministrationStateSelector = () => useAppSelector(getAdministrationStateSelector);
export const useEmployeesAdministrationStateSelector = () => useAppSelector(getEmployeesAdministrationStateSelector);
export const useDictionariesAdministrationStateSelector = () =>
  useAppSelector(getDictionariesAdministrationStateSelector);

export const useLocationsAdministrationState = () => useAppSelector(getLocationsAdministrationState);
export const useCitiesByLangAdministrationState = () => useAppSelector(getCitiesByLangAdministrationState);
export const useStatusesAdministrationState = () => useAppSelector(getStatusesAdministrationState);
export const useEmployeesEmailsOptions = () => useAppSelector(getEmployeesEmailsOptions);

export const useAdministrationStatusesAPI = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isLoading, currentStatus } = useStatusesAdministrationState();

  const statusesPhaseOptions = useMemo(() => {
    return Object.keys(CandidateStatusGroupEnum).map((item) => {
      return { title: t(`options.status_group_${item}`), value: item };
    });
  }, [t]);

  const createStatus = useCallback(
    (data: AdministrationStatusItem) => {
      return dispatch(createStatusThunk({ data }));
    },
    [dispatch],
  );

  const updateStatus = useCallback(
    (id: string, data: AdministrationStatusItem) => {
      return dispatch(updateStatusThunk({ id, data }));
    },
    [dispatch],
  );

  const getStatusById = useCallback(
    (id: string) => {
      return dispatch(getStatusItemByIdThunk({ id }));
    },
    [dispatch],
  );

  const deleteStatusById = useCallback(
    (id: string, priority: number) => {
      return dispatch(deleteStatusItemByIdThunk({ id, priority }));
    },
    [dispatch],
  );

  const restoreStatusById = useCallback(
    (id: string, priority: number) => {
      return dispatch(restoreStatusItemByIdThunk({ id, priority }));
    },
    [dispatch],
  );

  const clearCurrentStatus = useCallback(() => {
    dispatch(clearCurrentStatusAction());
  }, [dispatch]);

  const syncEventFromHrm = useCallback(
    (args: Parameters<typeof syncEventFromHrmThunk>[0]) => {
      return dispatch(syncEventFromHrmThunk(args));
    },
    [dispatch],
  );

  return {
    statusesPhaseOptions,
    clearCurrentStatus,
    getStatusById,
    createStatus,
    updateStatus,
    isLoading,
    currentStatus,
    deleteStatusById,
    restoreStatusById,
    syncEventFromHrm,
  };
};

export const useLocationsAdministrationAPI = () => {
  const dispatch = useAppDispatch();
  const state = useLocationsAdministrationState();

  const fetchCitiesByLanguages = useCallback(() => {
    return dispatch(fetchCitiesByLanguagesThunk());
  }, [dispatch]);

  const mergeLocations = useCallback(
    (data: MergeLocationsItem) => {
      return dispatch(mergeLocationsThunk({ data }));
    },
    [dispatch],
  );

  return { ...state, fetchCitiesByLanguages, mergeLocations };
};

export const useEmployeesAdministrationAPI = () => {
  const dispatch = useAppDispatch();
  const state = useEmployeesAdministrationStateSelector();

  const [searchValue, setSearchValue] = useState<string>('');
  const [empty, setEmpty] = useState<boolean>(false);

  const searchValueRef = useRef<string>(searchValue);
  searchValueRef.current = searchValue;

  const requestsOptions = useMemo(
    () => state.requestsOptions.ids.map((id) => state.requestsOptions.options[id]),
    [state.requestsOptions],
  );

  const vacanciesOptions = useMemo(
    () => state.vacanciesOptions.ids.map((id) => state.vacanciesOptions.options[id]),
    [state.vacanciesOptions],
  );

  const setPagable = useCallback(
    (...pagable: Parameters<typeof setPagableAction>) => dispatch(setPagableAction(...pagable)),
    [dispatch],
  );

  const searchEmployees = useCallback(
    (...args: Parameters<typeof searchEmployeesThunk>) => dispatch(searchEmployeesThunk(...args)),
    [dispatch],
  );

  const updateEmployeeAuthData = useCallback(
    (...args: Parameters<typeof updateEmployeeAuthDataThunk>) => {
      return dispatch(updateEmployeeAuthDataThunk(...args));
    },
    [dispatch],
  );

  const updateUserSelectedAuthDataPermissions = useCallback(
    (...arg: Parameters<typeof updateUserSelectedAuthDataPermissionsAction>) =>
      dispatch(updateUserSelectedAuthDataPermissionsAction(...arg)),
    [dispatch],
  );

  const selectAllUserAuthDataPermissions = useCallback(
    (...arg: Parameters<typeof selectAllUserAuthDataPermissionsAction>) =>
      dispatch(selectAllUserAuthDataPermissionsAction(...arg)),
    [dispatch],
  );

  const unSelectAllUserAuthDataPermissions = useCallback(
    (...arg: Parameters<typeof unSelectAllUserAuthDataPermissionsAction>) =>
      dispatch(unSelectAllUserAuthDataPermissionsAction(...arg)),
    [dispatch],
  );

  const setNewEmployeeRoleFromHrm = useCallback(
    (...args: Parameters<typeof setNewEmployeeRoleFromHrmAction>) => dispatch(setNewEmployeeRoleFromHrmAction(...args)),
    [dispatch],
  );

  const updateExistingUserRole = useCallback(
    (...args: Parameters<typeof updateExistingUserRoleAction>) => dispatch(updateExistingUserRoleAction(...args)),
    [dispatch],
  );

  const searchVacancyOptions = useCallback(
    async (args: Parameters<typeof searchVacancyOptionsThunk>[0]) => {
      setSearchValue(args);
      const options = await dispatch(searchVacancyOptionsThunk(args)).unwrap();
      setEmpty(options.length === 0);
    },
    [dispatch],
  );

  const searchPagableVacanciesOptions = useCallback(
    async (args: Parameters<typeof searchPagableVacanciesOptionsThunk>[0]) => {
      const options = await dispatch(searchPagableVacanciesOptionsThunk(args)).unwrap();

      setEmpty(options.length === 0);
    },
    [dispatch],
  );

  const searchRequestOptions = useCallback(
    (args: Parameters<typeof searchRequestOptionsThunk>[0]) => dispatch(searchRequestOptionsThunk(args)),
    [dispatch],
  );

  const setEmployeeFromHrmToStore = useCallback(
    (data: Parameters<typeof setEmployeeFromHrmToStoreAction>[0]) =>
      dispatch(setEmployeeFromHrmToStoreAction(newEmployeeMapper(data))),
    [dispatch],
  );

  const addEmployee = useCallback(
    (args: Parameters<typeof addEmployeeThunk>[0]) => dispatch(addEmployeeThunk(args)),
    [dispatch],
  );

  const searchEmailByEmailPart = useCallback(
    (args: Parameters<typeof searchEmailByEmailPartThunk>[0]) => {
      return ThunkEffects.takeLatest(
        dispatch(searchEmailByEmailPartThunk(args)),
        searchEmailByEmailPartThunk.typePrefix,
      );
    },
    [dispatch],
  );

  const rolePermissionAssign = useCallback(
    (args: Parameters<typeof rolePermissionAssignThunk>[0]) => dispatch(rolePermissionAssignThunk(args)),
    [dispatch],
  );

  const deleteEmployee = useCallback(
    (args: Parameters<typeof deleteEmployeeThunk>[0]) => dispatch(deleteEmployeeThunk(args)),
    [dispatch],
  );

  const resetEmployeeFromHrmByEmail = useCallback(() => dispatch(resetEmployeeFromHrmByEmailAction()), [dispatch]);

  const resetEmployeesAdministrationsState = useCallback(
    () => dispatch(resetEmployeesAdministrationsStateAction()),
    [dispatch],
  );

  const { lastElementRef: _lastElementRef, resetPageNum, pageNum } = useInfiniteScroll(state.optionsLoading);

  useEffect(() => {
    if (!searchValue) return;

    if (pageNum > 1) {
      searchPagableVacanciesOptions({
        params: { currentPage: pageNum, pageSize: 10, sort: SortingValue.VacancyNameASC },
        body: {
          searchBarRequest: searchValue,
          lifecycleStatus: LifecycleStatuses.Actual,
        },
      });
    }
  }, [pageNum]);

  useEffect(() => {
    resetPageNum();
  }, [searchValue]);

  const lastElementRef = useMemo(() => {
    return empty ? null : _lastElementRef;
  }, [empty]);

  const resetSearchVacanciesState = () => setSearchValue('');

  return {
    ...state,
    requestsOptions,
    vacanciesOptions,
    setPagable,
    deleteEmployee,
    addEmployee,
    searchEmployees,
    rolePermissionAssign,
    searchVacancyOptions,
    searchRequestOptions,
    updateExistingUserRole,
    searchEmailByEmailPart,
    updateEmployeeAuthData,
    setEmployeeFromHrmToStore,
    setNewEmployeeRoleFromHrm,
    resetEmployeesAdministrationsState,
    resetEmployeeFromHrmByEmail,
    selectAllUserAuthDataPermissions,
    unSelectAllUserAuthDataPermissions,
    updateUserSelectedAuthDataPermissions,
    lastElementRef,
    resetSearchVacanciesState,
  };
};

export const useDictionariesAdministrationAPI = () => {
  const dispatch = useAppDispatch();
  const state = useDictionariesAdministrationStateSelector();

  const setPagable = useCallback(
    (...pagable: Parameters<typeof setPagableAction>) => dispatch(setDictionaryPagableAction(...pagable)),
    [dispatch],
  );

  const searchDictionaryValues = useCallback(
    (params: {
      page: number;
      size?: number;
      searchText?: string;
      domainTypes?: Array<string>;
      parentIds?: string[];
      sortDirection?: string;
      sortLanguage?: string;
    }) => dispatch(searchDictionaryValuesThunk(params)),
    [dispatch],
  );

  const createDictionaryValue = useCallback(
    (params: { languageTranslationMapping: DictionaryLanguageTranslation; domainTypename: string; parentId?: string }) =>
      DictionaryService.createDictionaryValues(params),
    [],
  );

  const updateDictionaryValue = useCallback(
    (params: {
      languageTranslationMapping: DictionaryLanguageTranslation;
      domainTypename: string;
      unionTag: string;
      parentId?: string;
    }) =>
      DictionaryService.updateDictionaryValues(params),
    [],
  );

  const selectDomainTypename = useCallback(
    (domainType: string) => {
      dispatch(selectDomainTypeAction(domainType));
    },
    [dispatch],
  );

  const selectDictionaryCandidateSource = useCallback(
    (candidateSourceId: string) => {
      dispatch(selectDictionaryCandidateSourceAction(candidateSourceId));
    },
    [dispatch],
  );

  const setDictionarySearchValue = useCallback(
    (searchValue: string) => {
      dispatch(setDictionarySearchValueAction(searchValue || ''));
    },
    [dispatch],
  );

  const setSortingOrder = useCallback(
    (sortingCode: string) => {
      const [language, direction] = sortingCode.split('_');

      dispatch(
        setDictionarySortParamsAction({ sortLanguage: language as DictionaryLanguage, sortDirection: direction }),
      );
    },
    [dispatch],
  );

  return {
    ...state,
    selectDomainTypename,
    selectDictionaryCandidateSource,
    setDictionarySearchValue,
    searchDictionaryValues,
    createDictionaryValue,
    updateDictionaryValue,
    setPagable,
    setSortingOrder,
  };
};

export const useReferralsAdministrationState = (): ReferralState => useAppSelector(getReferralsAdministrationState);

export const useReferralsAdministrationManagerState = (): LocalizedShortEmployeeItemResponse =>
  useAppSelector(getReferralsAdministrationManagerState);

export const useReferralAPI = () => {
  const dispatch = useAppDispatch();
  const state = useReferralsAdministrationState();
  const { profile } = useProfileAPI();
  const { manager } = state;

  const allowedToAgree = useMemo(() => {
    if (!profile) return false;
    return manager.employeeId === profile.id;
  }, [manager, profile]);

  const getReferralsTotalData = useCallback(
    (args: Parameters<typeof getReferralsTotalDataThunk>[0]) => {
      return ThunkEffects.takeLatest(dispatch(getReferralsTotalDataThunk(args)), getReferralsTotalDataThunk.typePrefix);
    },
    [dispatch],
  );

  const getReferralsData = useCallback(
    (args: Parameters<typeof getReferralDataThunk>[0]) => {
      return ThunkEffects.takeLatest(
        dispatch(getReferralDataThunk(args)),
        getReferralDataThunk.typePrefix + args.yearMonth,
      );
    },
    [dispatch],
  );

  const exportReferralsData = useCallback(
    (args: Parameters<typeof exportReferralDataThunk>[0]) => {
      return dispatch(exportReferralDataThunk(args));
    },
    [dispatch],
  );

  const updateReferralsManager = useCallback(
    (args: Parameters<typeof updateReferralsManagerThunk>[0]) => {
      return dispatch(updateReferralsManagerThunk(args));
    },
    [dispatch],
  );

  const resolveReferralStatus = useCallback(
    (data: { yearMonth: string; id: string; accepted: boolean }) => {
      return dispatch(resolveReferralStatusThunk(data));
    },
    [dispatch],
  );

  const getReferralsManager = useCallback(() => {
    dispatch(getReferralsManagerThunk());
  }, [dispatch]);

  const getReferralsManagerOptions = useCallback(
    (roles: RolesValues[]) => {
      dispatch(getReferralManagerOptionsThunk({ roles }));
    },
    [dispatch],
  );

  const clearReferralsData = useCallback(() => {
    dispatch(clearReferralStateAction());
  }, [dispatch]);

  const closeReferralPaymentPeriod = useCallback(
    (args: Parameters<typeof closeReferralPaymentPeriodThunk>[0]) => {
      return dispatch(closeReferralPaymentPeriodThunk(args));
    },
    [dispatch],
  );

  const closeReferralPayment = useCallback(
    (args: Parameters<typeof closeReferralPaymentThunk>[0]) => {
      return dispatch(closeReferralPaymentThunk(args));
    },
    [dispatch],
  );

  const transferReferralPayment = useCallback(
    (args: Parameters<typeof transferReferralPaymentThunk>[0]) => {
      return dispatch(transferReferralPaymentThunk(args));
    },
    [dispatch],
  );

  return {
    ...state,
    allowedToAgree,
    exportReferralsData,
    getReferralsTotalData,
    getReferralsData,
    getReferralsManagerOptions,
    updateReferralsManager,
    getReferralsManager,
    resolveReferralStatus,
    clearReferralsData,
    closeReferralPaymentPeriod,
    closeReferralPayment,
    transferReferralPayment,
  };
};

export const useUnitsAdministrationState = (): AdministrationUnitsState => useAppSelector(getUnitsAdministrationState);
export const useUnitsAdministrationOptions = (): SelectOption[] => useAppSelector(getUnitsAdministrationOptions);

export const useUnitsAPI = () => {
  const dispatch = useAppDispatch();
  const state = useUnitsAdministrationState();
  const unitsOptions = useUnitsAdministrationOptions();

  const createUnit = useCallback(
    (data: UnitItem) => {
      return dispatch(createUnitThunk(data));
    },
    [dispatch],
  );

  const updateUnit = useCallback(
    (data: UnitItem) => {
      return dispatch(updateUnitThunk(data));
    },
    [dispatch],
  );

  const getAllUnits = useCallback(() => {
    return dispatch(getAllUnitsThunk());
  }, [dispatch]);

  const getActualUnits = useCallback(() => {
    return dispatch(getActualUnitsThunk());
  }, [dispatch]);

  const clearUnitsState = useCallback(() => {
    dispatch(clearUnitsStateAction());
  }, [dispatch]);

  return { ...state, unitsOptions, createUnit, updateUnit, getAllUnits, getActualUnits, clearUnitsState };
};

const useSourcingAdministrationState = (): AdministrationSourcingState =>
  useAppSelector(getSourcingAdministrationState);
export const useSourcingDefaultFormItemById = (id: string): SourcingItem | null =>
  useAppSelector(getSourcingDefaultFormItemById(id));

export const useSourcingAPI = () => {
  const dispatch = useAppDispatch();
  const state = useSourcingAdministrationState();

  const createSourcingEmployee = useCallback(
    (args: Parameters<typeof createSourcingEmployeeThunk>[0]) => {
      return dispatch(createSourcingEmployeeThunk(args));
    },
    [dispatch],
  );

  const updateSourcingEmployee = useCallback(
    (args: Parameters<typeof updateSourcingEmployeeThunk>[0]) => {
      return dispatch(updateSourcingEmployeeThunk(args));
    },
    [dispatch],
  );

  const deleteSourcingEmployee = useCallback(
    (employeeId: string) => {
      return dispatch(createSourcingEmployeeThunk({ requiredNumberOfEvents: '0', employeeId }));
    },
    [dispatch],
  );

  const getAllSourcingData = useCallback(
    (args: Parameters<typeof getAllSourcingDataThunk>[0]) => {
      return dispatch(getAllSourcingDataThunk(args));
    },
    [dispatch],
  );

  const clearSourcingState = useCallback(() => {
    return dispatch(clearSourcingStateAction());
  }, [dispatch]);

  return {
    ...state,
    createSourcingEmployee,
    updateSourcingEmployee,
    deleteSourcingEmployee,
    clearSourcingState,
    getAllSourcingData,
  };
};

export const useAdministrationRequestApproveState = (): AdministrationRequestApproveState =>
  useAppSelector((state) => state.administration.requestApprove);

export const useAdministrationRequestApproveAPI = () => {
  const dispatch = useAppDispatch();
  const state = useAdministrationRequestApproveState();

  const getAdministrationRequestApprove = useCallback(() => {
    return dispatch(getAdministrationRequestApproveThunk());
  }, [dispatch]);

  const setAdministrationRequestApprove = useCallback(
    async (args: Parameters<typeof StatusesService.setAdministrationRequestApprove>[0]) => {
      return (await StatusesService.setAdministrationRequestApprove(args))?.data;
    },
    [],
  );

  const setAdministrativeRequestClarify = useCallback(async (claimId: number, confirm: boolean) => {
    return (await StatusesService.setAdministrativeRequestClarify(claimId, confirm))?.data;
  }, []);

  const setAdministrativeRequestReClarify = useCallback(async (claimId: number) => {
    return (await StatusesService.setAdministrativeRequestReClarify(claimId))?.data;
  }, []);

  return {
    ...state,
    getAdministrationRequestApprove,
    setAdministrationRequestApprove,
    setAdministrativeRequestClarify,
    setAdministrativeRequestReClarify,
  };
};
